/**
 * OptionChips — pick one option from a small, known set, as pill chips.
 *
 * The unit picker is the highest-frequency choice in patient administration
 * (every admit and every transfer goes through it) and it was a bare native
 * `<select>`: the iOS wheel on a phone, a Windows dropdown on the terminal,
 * two taps minimum, and a 6px radius sitting inside 18px cards. The house
 * already had the right shape for "choose one of a few" — the allocation
 * board's nurse picker (components/allocation/AllocationBoard.tsx) — so this
 * is that shape, made shared: pill chips, 44px targets, `scale(0.95)` press,
 * one tap.
 *
 * Selection is never carried by colour alone: the selected chip states itself
 * with a check glyph and `aria-pressed` as well as the Action-Blue fill.
 *
 * Chips are for a set a nurse can take in at once. Beyond that they stop being
 * an improvement — forty wrapped pills are worse than a list — so past
 * `chipLimit` this falls back to a real select, on the radius ladder. The
 * pilot's unit list (a handful of leaf units) and every disposition vocabulary
 * are comfortably under it; a hospital-wide location tree is not, and that
 * case must not degrade into a wall.
 */

import { useTranslation } from 'react-i18next'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Options above which chips stop being scannable and a list serves better.
 *  A presentation cap, not a domain limit. */
const DEFAULT_CHIP_LIMIT = 8

export interface OptionChoice {
  value: string
  label: string
}

export interface OptionChipsProps {
  options: OptionChoice[]
  /** The chosen value, or '' for nothing chosen yet. */
  value: string
  onChange: (value: string) => void
  /** Labels the group for screen readers — the visible <label> text. */
  legend: string
  /** Ties the fallback select to the caller's own <label htmlFor>. */
  id?: string
  disabled?: boolean
  /** Rendered when the option set is empty (a vocabulary that failed to load
   *  is the caller's own error state — this is only "nothing to choose"). */
  emptyLabel?: string
  /** Placeholder row for the fallback select. */
  placeholder?: string
  chipLimit?: number
  className?: string
}

export function OptionChips({
  options,
  value,
  onChange,
  legend,
  id,
  disabled = false,
  emptyLabel,
  placeholder,
  chipLimit = DEFAULT_CHIP_LIMIT,
  className,
}: OptionChipsProps) {
  const { t } = useTranslation()

  if (options.length === 0) {
    return (
      <p className="text-[13px] text-neutral-500" role="status">
        {emptyLabel ?? t('common.loading')}
      </p>
    )
  }

  if (options.length > chipLimit) {
    return (
      <select
        id={id}
        aria-label={legend}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'flex min-h-11 w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[15px]',
          'ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          className,
        )}
      >
        <option value="">{placeholder ?? legend}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    )
  }

  return (
    <div
      role="group"
      aria-label={legend}
      className={cn('flex flex-wrap items-center gap-2', className)}
    >
      {options.map((option) => {
        const selected = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            disabled={disabled}
            aria-pressed={selected}
            className={cn(
              'inline-flex min-h-[44px] max-w-full items-center gap-1.5 rounded-full border px-4',
              'text-[14px] font-semibold transition active:scale-95 disabled:opacity-50',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
              selected
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-neutral-200 bg-white text-neutral-900 hover:border-neutral-300 hover:bg-neutral-50',
            )}
          >
            {selected && <Check className="h-4 w-4 shrink-0" aria-hidden="true" />}
            <span className="truncate">{option.label}</span>
          </button>
        )
      })}
    </div>
  )
}
